import { useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import cn from "classnames";
import { ErrorMessage } from "components/ui/ErrorMessage";
import { Input } from "components/ui/Input";
import { PhoneCode } from "components/ui/PhoneCode/PhoneCode";
import { PhoneSelectList } from "components/ui/PhoneSelectList";
import { SITE_NAME } from "constants/siteName";
import { useSiteName } from "context/SiteName.context";
import { useClickOutside } from "hooks/useClickOutside";
import style from "./PhoneSelect.module.scss";

type Country = {
  name: string;
  iso: string;
  countryCode: string;
};

type PhoneSelectProps = {
  userCountry: Country;
  className?: string;
  isCountryLoading: boolean;
};

export const PhoneSelect = ({ userCountry, className, isCountryLoading }: PhoneSelectProps) => {
  const [isOpenList, setIsOpenList] = useState<boolean>(false);
  const [mainNumber, setMainNumber] = useState("");
  const phoneSelectRef = useRef<HTMLDivElement>(null);
  const [countryData, setCountryData] = useState<Country>({
    name: "",
    iso: "",
    countryCode: "",
  });

  const { control, setValue } = useFormContext();
  const { siteName } = useSiteName();
  useClickOutside(phoneSelectRef, () => setIsOpenList(false));
  useEffect(() => setCountryData(userCountry), [userCountry]);

  const onCountryCodeChange = (country: Country) => {
    setCountryData(country);
    setIsOpenList(false);
    setMainNumber("");
  };

  const onMainNumberChange = (value: string, onChange: (value: string) => void) => {
    const number = value.replace(/\D/g, "");
    if (countryData && number.length + countryData.iso.length <= 16) {
      onChange(number);
      setMainNumber(number);
      if (!!number.length) {
        setValue("phone", `${countryData.iso + number}`);
      } else {
        setValue("phone", "");
      }
    }
  };

  return (
    <div ref={phoneSelectRef} className={className}>
      <Controller
        name="phone"
        control={control}
        render={({ field: { name, onChange, onBlur }, fieldState: { error } }) => (
          <div className={style.phoneSelectWrapper}>
            <div className={style.phoneSelectLableWrapper}>
              <label className={style.phoneSelectLabel}>Phone number</label>
            </div>
            <div className={style.phoneSelectInputWrapper}>
              <div
                className={cn(style.phoneFieldShell, {
                  [style["phoneFieldShell--invalid"]]: error,
                  [style["phoneFieldShell--chinese-brothers"]]: siteName === SITE_NAME.CHINESE_BROTHERS,
                  [style["phoneFieldShell--qubictron"]]: siteName === SITE_NAME.QUBICTRON,
                })}
              >
                <PhoneCode
                  countryData={countryData}
                  isOpenList={isOpenList}
                  setIsOpenList={setIsOpenList}
                  isCountryLoading={isCountryLoading}
                />
                <div className={style.phoneFieldDivider} aria-hidden />
                <div className={style.phoneFieldInputWrap}>
                  <Input
                    name={name}
                    type="tel"
                    embedded
                    placeholder="(000) 00 00 000"
                    onBlur={onBlur}
                    onChange={(e) => onMainNumberChange(e.currentTarget.value, onChange)}
                    value={mainNumber || ""}
                    className={className}
                  />
                </div>
                {isOpenList && <PhoneSelectList onCountryCodeChange={onCountryCodeChange} />}
              </div>
              <ErrorMessage error={error} />
            </div>
          </div>
        )}
      />
    </div>
  );
};
